import { type ComponentProps, useEffect, useRef, useState } from 'react';

import { Alert, AlertTitle, Box, Button, Fade, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player';
import { type OnProgressProps } from 'react-player/base';
import { useDebounce } from 'usehooks-ts';

const Container = styled(Box)`
    width: 100%;
    height: 100%;
    position: relative;
`;

const NoClickOverlay = styled(Box)`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
`;

const StyledAlert = styled(Alert)`
    top: 12px;
    right: 12px;
    position: absolute;
    z-index: 1;
`;

export interface VideoPlayerState {
    playing: boolean;
    playbackRate: number;
    timestamp: number;
}

export interface SyncedVideoPlayerProps extends ComponentProps<typeof ReactPlayer> {
    videoUrl: string;
    isController: boolean;
    controlType: 'student' | 'teacher';

    // Called when the controller updates the player state for everybody
    onControllerStateUpdate?: (playerState: VideoPlayerState) => void;

    // Called when the component asks to receive a fresh video state, i.e. due to unstarted status because of no user interaction
    onRefreshVideoState?: () => void | Promise<void>;

    // This can be used to pass down the current state set by the controller.
    sharedState?: VideoPlayerState;

    sharerName: string;
}

export const SyncedVideoPlayer = (props: SyncedVideoPlayerProps) => {
    const {
        videoUrl,
        controlType,
        isController,
        sharerName,
        onControllerStateUpdate,
        onRefreshVideoState,
        sharedState,
        ...other
    } = props;
    const { t } = useTranslation('playvideo');

    const canHandleVideo = isController || controlType === 'student';

    const reactPlayerRef = useRef<ReactPlayer>(null);

    // load the video in muted state by default, if the user has no control over this video
    // https://developer.chrome.com/blog/autoplay/#new-behaviors
    const [isVideoMuted, setIsVideoMuted] = useState(!canHandleVideo);

    // These three states are for video error handling on start.
    // null describes that the video didn't attempt to start at all,
    // `launched` describes that the video has started playing at some point and any launch errors have been resolved,
    // `unstarted` describes that there was a launch error and the user needs to click on the video to start it manually.
    const [videoLaunchStatus, setVideoLaunchStatus] = useState<null | 'launched' | 'unstarted'>(null);
    const unstartedAlertTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // The video state. There are two ways this can be updated:
    // 1. If the user is the controller, they update this themselves when interacting with the video. This is then passed
    // via the `onControllerStateUpdate` callback upstream.
    // 2. The user is not the controller, in which case this is being updated from upstream using the `sharedState` prop.
    const [videoState, setVideoState] = useState<Omit<VideoPlayerState, 'timestamp'>>({
        playing: false,
        playbackRate: 1.0
        // note that this is without the timestamp - we add the timestamp whenever onControllerStateUpdate gets called
        // to make sure it's up to date
    });

    const onPlay = async () => {
        console.log('[SyncedVideoPlayer] Video has started playing');

        if (videoLaunchStatus === 'unstarted' && !canHandleVideo) {
            // there was an error playing the video automatically and the user has clicked on the play button.
            // make sure they're on the correct state and time by requesting a refresh of the video state.
            await onRefreshVideoState?.();
        }

        setVideoLaunchStatus('launched');

        if (unstartedAlertTimeoutRef.current) {
            // the video has started despite it throwing errors before
            clearTimeout(unstartedAlertTimeoutRef.current);
            unstartedAlertTimeoutRef.current = null;
        }

        if (!isController) {
            return;
        }

        setVideoState((current) => ({
            ...current,
            playing: true
        }));
    };

    const onPause = () => {
        console.log('[SyncedVideoPlayer] Video has been paused');

        if (!isController) {
            return;
        }

        setVideoState((current) => ({
            ...current,
            playing: false
        }));
    };

    // onPause should suffice, but there's a bug in react-player (or the youtube player) that pauses the video automatically
    // for other participants when the controller presses the start from beginning button.
    // as a workaround, we seek back to 0.
    // as a small benefit it also does not show the "related" videos at the end.
    const onEnded = () => {
        console.log('[SyncedVideoPlayer] Video has ended, seeking to 0');

        setVideoState((current) => ({
            ...current,
            playing: false
        }));

        reactPlayerRef.current?.seekTo(0);
    };

    const onProgress = ({ playedSeconds }: OnProgressProps) => {
        if (!isController) {
            return;
        }

        // the onProgress callback is triggered once a second whenever the video playback time (played seconds) changes.
        // We don't need that, because the onPlay/onPause callbacks do all we need, including seeking (both onPause/onPlay is triggered on seek)
        // However, these two callbacks don't get triggered when the video is paused and so seek time wouldn't update during paused video.

        if (!videoState.playing) {
            console.log(`[SyncedVideoPlayer] Seeking paused video to ${playedSeconds}`);
            // we only need to create a new object so `stateToSendThrottled` gets updated. The current time is used in the `onControllerStateUpdate` useEffect.
            setVideoState((current) => ({ ...current }));
        }
    };

    const onPlaybackRateChange = (newRate: number) => {
        if (!isController) {
            return;
        }

        setVideoState((current) => ({ ...current, playbackRate: newRate }));
    };

    const onVideoUnstarted = () => {
        // for some reason this also sometimes shows up for controllable videos, but it works anyway
        if (canHandleVideo) {
            console.log('[SyncedVideoPlayer] Received onUnstarted but the video is user controlled. Ignoring');
            return;
        }

        if (videoLaunchStatus === 'launched') {
            console.log('[SyncedVideoPlayer] Received onUnstarted but the video has already started playing. Ignoring');
            return;
        }

        console.warn(
            '[SyncedVideoPlayer] Failed to play video. Likely an issue with autoplay. Allowing click to start playback'
        );

        if (unstartedAlertTimeoutRef.current) {
            // the error tends to fire multiple times - clear the previous timeout
            clearTimeout(unstartedAlertTimeoutRef.current);
        }

        // do this in a timeout, as sometimes the youtube player throws an unstarted error even though the video starts playing eventually.
        // this timeout is cleared in the onPlay callback.
        unstartedAlertTimeoutRef.current = setTimeout(() => {
            setVideoLaunchStatus('unstarted');
        }, 3000);
    };

    const stateToSendThrottled = useDebounce(videoState, 1000);

    useEffect(() => {
        if (isController) {
            onControllerStateUpdate?.({
                ...stateToSendThrottled,
                // always send the most up to date timestamp
                timestamp: reactPlayerRef.current?.getCurrentTime() ?? 0
            });
        }
    }, [isController, stateToSendThrottled, onControllerStateUpdate]);

    useEffect(() => {
        if (!isController && sharedState) {
            console.log('[SyncedVideoPlayer] Received new video state, updating', sharedState);
            // we're not the video controller and new expected video state was received - update it
            setVideoState(sharedState);

            if (sharedState.playing || sharedState.timestamp > 0) {
                setTimeout(() => {
                    console.log(`[SyncedVideoPlayer] Seeking player to ${sharedState.timestamp}`);
                    reactPlayerRef.current?.seekTo(sharedState.timestamp, 'seconds');
                });
            }
        }
    }, [isController, sharedState]);

    return (
        <Container>
            <Fade in={!canHandleVideo && isVideoMuted && videoLaunchStatus === 'launched'}>
                <StyledAlert
                    severity="success"
                    action={<Button onClick={() => setIsVideoMuted(false)}>{t('playback-alert-muted-action')}</Button>}
                >
                    <AlertTitle>
                        {t('playback-alert-muted-title', { sharerName, interpolation: { escapeValue: false } })}
                    </AlertTitle>
                    {t('playback-alert-muted-text')}
                </StyledAlert>
            </Fade>
            <Fade in={!canHandleVideo && videoLaunchStatus === 'unstarted'}>
                <StyledAlert severity="success">
                    <AlertTitle>
                        {t('playback-alert-unstarted-title', { sharerName, interpolation: { escapeValue: false } })}
                    </AlertTitle>
                    {t('playback-alert-unstarted-text')}
                </StyledAlert>
            </Fade>

            {/* We allow clicking on the video to start playing it if the status is unstarted - this requires a user interaction with the player. */}
            {!canHandleVideo && videoLaunchStatus !== 'unstarted' && <NoClickOverlay />}
            <ReactPlayer
                ref={reactPlayerRef}
                url={videoUrl}
                controls={canHandleVideo}
                muted={isVideoMuted}
                config={{
                    youtube: {
                        playerVars: {
                            controls: canHandleVideo ? 1 : 0,
                            disablekb: !canHandleVideo,
                            // this is supposed to remove annotations but does not seem to be doing anything.
                            iv_load_policy: 3,
                            fs: 0
                        },
                        onUnstarted: onVideoUnstarted
                    },
                    vimeo: {
                        playerOptions: {
                            controls: canHandleVideo,
                            keyboard: canHandleVideo
                        }
                    }
                }}
                onStart={onPlay}
                onPlay={onPlay}
                onPause={onPause}
                onEnded={onEnded}
                onProgress={onProgress}
                // onSeek does not work with youtube, but we don't need it because youtube triggers pause/play after seeking.
                onPlaybackRateChange={onPlaybackRateChange}
                // if we're not the controller, update the state in the video whenever it changes.
                {...(isController ? {} : videoState)}
                {...other}
            />
        </Container>
    );
};
